import { useCallback, useEffect, useRef, useState } from 'react';

import { ErrorIcon, InfoIcon, SuccessIcon, UploadIcon, WarningIcon } from '../Icon';
import { RequiredStateText } from '../RequiredStateText';
import { Spinner } from '../Spinner';
import { TextLink } from '../TextLink';
import Tooltip from '../Tooltip/Tooltip';
import { getFileIconForExtension } from '../../utils';
import './FileUpload.css';

export interface UploadedFile {
  /**
   * Unique identifier for the uploaded file
   */
  id: string;
  /**
   * The native File object. Persisted server-side document refs may not have
   * a browser File after hydration.
   */
  file?: File;
  /**
   * Display name of the file
   */
  name: string;
  /**
   * File size in bytes
   */
  size: number;
  /**
   * If present, indicates an error occurred with this file (e.g., upload error)
   */
  error?: string;
  /**
   * If true, indicates the file is currently being uploaded
   */
  isUploading?: boolean;
  /**
   * Backend document record ID once associated with an application.
   */
  documentId?: string;
  /**
   * Backend document type once associated with an application.
   */
  documentType?: string;
  /** Backend classification label used to restore grouped optional files. */
  documentLabel?: string;
  /**
   * MIME type persisted for the document.
   */
  mimeType?: string;
  /**
   * Backend malware scan status.
   */
  scanStatus?: string;
  /**
   * Upload/association status for workflow validation.
   */
  uploadStatus?: 'idle' | 'uploading' | 'associated' | 'failed';
  /**
   * Server upload timestamp or confirmation timestamp.
   */
  uploadedAt?: string;
  /**
   * When true, the file is already persisted on the server and the {@link file}
   * blob is not available. Suppresses the "unreadable file" warning.
   */
  isExisting?: boolean;
}

export interface FileUploadError {
  /**
   * Name of the file that caused the error
   */
  fileName: string;
  /**
   * Reason for the error
   */
  reason: 'count' | 'format' | 'size';
  /**
   * Human-readable error message
   */
  message: string;
}

export interface FileUploadProps {
  /**
   * Array of accepted file formats (e.g., ['PDF', 'DOC', 'DOCX'])
   * @default ['PDF']
   */
  acceptedFormats?: string[];
  /**
   * Maximum file size allowed in megabytes
   * @default 4
   */
  maxSizeMB?: number;
  /**
   * Maximum number of files allowed. When set to 1, choosing or dropping a file replaces the current file.
   * When omitted, new files are appended to the list.
   */
  maxFiles?: number;
  /**
   * Callback when files are added or removed
   */
  onFilesChange: (files: UploadedFile[]) => void;
  /**
   * Array of currently uploaded files
   * @default []
   */
  files?: UploadedFile[];
  /**
   * Hides the uploaded-file list while retaining the upload controls.
   * @default false
   */
  hideFileList?: boolean;
  /**
   * Error for the upload as a whole — a required document that is missing. Reported to
   * screen readers only (ACP-2092): the upload area is marked invalid and described by
   * the message, so the workflow can name it and move focus to it after a rejected
   * Next or Save. Nothing changes on screen.
   */
  errorMessage?: string;
  /**
   * Hides the label, dropzone, and format guidance while retaining the uploaded-file list.
   * Useful when one uploader manages several persistent categorized lists.
   * @default false
   */
  hideUploadControls?: boolean;
  /**
   * Label text for the file upload component
   */
  label?: string;
  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;
  /**
   * Tooltip info text to display next to the label
   */
  tooltipText?: string;
  /**
   * Names the upload controls when no visible label is rendered. Without it every
   * upload area on a page announces identically ("Upload file area. Click or press
   * Enter to choose a file..."), leaving a screen reader user unable to tell which
   * section they are in.
   */
  ariaLabel?: string;
  /**
   * Optional callback when a file is rejected due to format or size constraints
   */
  onFileError?: (error: FileUploadError) => void;
  /**
   * Optional callback when a file is removed. Passes the removed file.
   */
  onFileRemove?: (file: UploadedFile) => void;
  /**
   * Optional callback when an uploaded file name is clicked.
   */
  onFileClick?: (file: UploadedFile) => void;
  /**
   * When true, a locally selected file that still has its bytes is also clickable,
   * so it can be opened before it reaches the server. Server-stored files always
   * require scanStatus CLEAN.
   * @default false
   */
  allowLocalFilePreview?: boolean;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Indicates whether the filename should display as a link. Persisted
 * files in S3 require a completed, clean scan; `allowLocalFilePreview`
 * additionally opts in files whose bytes are still available client-side.
 */
export function isUploadedFilePreviewable(file: UploadedFile, options?: { allowLocalFilePreview?: boolean }): boolean {
  if (file.error || file.isUploading) {
    return false;
  }

  if (options?.allowLocalFilePreview && file.file != null && file.file.size > 0) {
    return true;
  }

  return Boolean(file.documentId) && file.scanStatus === 'CLEAN';
}

function getScanStatusNotice(scanStatus?: string) {
  if (scanStatus === 'PENDING') {
    return {
      message: 'Upload complete. Awaiting malware scan.',
      variant: 'warning' as const,
    };
  }

  if (scanStatus === 'SCANNING') {
    return {
      message: 'The document has been uploaded and is awaiting malware/virus scan',
      variant: 'warning' as const,
    };
  }

  if (scanStatus === 'CLEAN') {
    return {
      message: 'Scan complete. No threats found.',
      variant: 'success' as const,
    };
  }

  if (scanStatus === 'INFECTED') {
    return {
      message: 'The document is infected with a virus or malware and cannot be used. Please upload a new document.',
      variant: 'error' as const,
    };
  }

  if (scanStatus === 'ERROR') {
    return {
      message: 'The malware scan failed. Remove this document and upload it again.',
      variant: 'error' as const,
    };
  }

  return null;
}

export function FileUpload({
  acceptedFormats = ['PDF'],
  maxFiles,
  maxSizeMB = 4,
  onFilesChange,
  files = [],
  hideFileList = false,
  hideUploadControls = false,
  errorMessage,
  label = 'Upload Position Designation Tool (PDT)',
  required = false,
  tooltipText,
  ariaLabel,
  onFileError,
  onFileClick,
  onFileRemove,
  allowLocalFilePreview = false,
  'data-testid': dataTestId,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const focusAfterRemoveRef = useRef(false);
  const baseTestId = dataTestId ?? 'file-upload';
  const inputId = `${baseTestId}-input`;
  const controlName = label || ariaLabel || '';
  const tooltipId = `${baseTestId}-tooltip`;
  const errorId = `${baseTestId}-error`;

  const acceptString = acceptedFormats.map((f) => `.${f.toLowerCase()}`).join(',');
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;

      const newFiles: UploadedFile[] = [];

      Array.from(fileList).forEach((file) => {
        const extension = file.name.split('.').pop()?.toUpperCase();
        const isValidFormat = acceptedFormats.some((f) => f.toUpperCase() === extension);

        if (!isValidFormat) {
          const errorMessage = `The file type is not supported. Accepted formats: ${acceptedFormats
            .map((f) => f.toUpperCase())
            .join(', ')}.`;
          console.warn(errorMessage);
          onFileError?.({
            fileName: file.name,
            message: errorMessage,
            reason: 'format',
          });

          newFiles.push({
            error: errorMessage,
            file,
            id: generateId(),
            name: file.name,
            size: file.size,
          });

          return;
        }

        if (file.size > maxSizeBytes) {
          const errorMessage = `The Maximum file size allowed is ${maxSizeMB} MB.`;
          console.warn(errorMessage);
          onFileError?.({
            fileName: file.name,
            message: errorMessage,
            reason: 'size',
          });

          newFiles.push({
            error: errorMessage,
            file,
            id: generateId(),
            name: file.name,
            size: file.size,
          });

          return;
        }

        newFiles.push({
          file,
          id: generateId(),
          name: file.name,
          size: file.size,
        });
      });

      if (newFiles.length > 0) {
        if (maxFiles === 1) {
          files.forEach((existingFile) => {
            onFileRemove?.(existingFile);
          });
          onFilesChange(newFiles.slice(0, 1));

          return;
        }

        const availableSlots = maxFiles === undefined ? newFiles.length : Math.max(0, maxFiles - files.length);
        const acceptedFiles = newFiles.slice(0, availableSlots);

        if (acceptedFiles.length < newFiles.length && maxFiles !== undefined) {
          onFileError?.({
            fileName: newFiles[availableSlots]?.name ?? '',
            message: `You may upload up to ${maxFiles} documents in this section.`,
            reason: 'count',
          });
        }

        if (acceptedFiles.length > 0) {
          onFilesChange([...files, ...acceptedFiles]);
        }
      }
    },
    [acceptedFormats, files, maxFiles, maxSizeBytes, maxSizeMB, onFileRemove, onFilesChange, onFileError],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFiles],
  );

  const handleChooseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      const fileToRemove = files.find((f) => f.id === fileId);
      focusAfterRemoveRef.current = true;
      onFilesChange(files.filter((f) => f.id !== fileId));

      if (fileToRemove) {
        onFileRemove?.(fileToRemove);
      }
    },
    [files, onFilesChange, onFileRemove],
  );

  /**
   * ACP-2092 (Section 508): Remove unmounts the link that was pressed, so focus fell to
   * the document and a keyboard user was sent back to the top of the page. Land on the
   * upload area instead — the natural next step after removing a file — or, while it is
   * hidden, on the next file's Remove link.
   */
  useEffect(() => {
    if (!focusAfterRemoveRef.current) return;
    focusAfterRemoveRef.current = false;

    const root = rootRef.current;
    if (!root) return;

    const target =
      root.querySelector<HTMLElement>(`[data-testid="${baseTestId}-dropzone"]`) ??
      root.querySelector<HTMLElement>(`a[data-testid^="${baseTestId}-file-remove-"]`);

    target?.focus();
  }, [files, baseTestId]);

  return (
    <div
      ref={rootRef}
      className='file-upload'
      data-testid={baseTestId}>
      {!hideUploadControls && (
        <>
          <div
            className='file-upload__label-wrapper'
            data-testid={`${baseTestId}-label`}>
            {label && (
              <label
                className='ds-c-label file-upload__label'
                htmlFor={inputId}>
                <span className='file-upload__label-content'>
                  {label}
                  {(required || tooltipText) && (
                    <span className='file-upload__label-suffix'>
                      {required && (
                        <>
                          <span
                            aria-hidden='true'
                            className='file-upload__required-asterisk'>
                            *
                          </span>
                          <RequiredStateText />
                        </>
                      )}
                      {tooltipText && (
                        <Tooltip
                          className='file-upload__info-tooltip'
                          id={tooltipId}
                          placement='top'
                          title={tooltipText}>
                          <InfoIcon />
                        </Tooltip>
                      )}
                    </span>
                  )}
                </span>
              </label>
            )}
          </div>

          <div
            aria-describedby={errorMessage ? errorId : undefined}
            aria-invalid={errorMessage ? 'true' : undefined}
            aria-label={`Upload file area${
              controlName ? ` for ${controlName}` : ''
            }. Click or press Enter to choose a file, or drag and drop a file here.`}
            className={`file-upload__dropzone ${isDragging ? 'file-upload__dropzone--dragging' : ''}`}
            data-error-name={
              errorMessage && controlName
                ? /\brequired\b/i.test(controlName)
                  ? controlName
                  : `${controlName} required`
                : undefined
            }
            data-testid={`${baseTestId}-dropzone`}
            role='button'
            tabIndex={0}
            onClick={handleChooseFile}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleChooseFile();
              }
            }}>
            <div
              className='file-upload__icon'
              data-testid={`${baseTestId}-icon`}>
              <UploadIcon size={24} />
            </div>
            <p
              className='file-upload__text'
              data-testid={`${baseTestId}-text`}>
              Drag and drop files here or{' '}
              <button
                aria-label={controlName ? `choose a file for ${controlName}` : undefined}
                className='file-upload__choose-link'
                data-testid={`${baseTestId}-choose`}
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  handleChooseFile();
                }}>
                choose a file
              </button>
              .
            </p>
            <input
              ref={fileInputRef}
              accept={acceptString}
              aria-label={label ? undefined : controlName || 'Choose a file to upload'}
              aria-required={required ? 'true' : 'false'}
              className='file-upload__input'
              data-testid={inputId}
              id={inputId}
              multiple={maxFiles !== 1}
              required={required}
              tabIndex={-1}
              type='file'
              onChange={handleInputChange}
            />
          </div>

          {errorMessage && (
            <span
              className='ds-u-visibility--screen-reader'
              data-testid={`${baseTestId}-error`}
              id={errorId}>
              {errorMessage}
            </span>
          )}

          <div
            className='file-upload__info'
            data-testid={`${baseTestId}-info`}>
            <span data-testid={`${baseTestId}-formats`}>
              Supported formats: {acceptedFormats.map((f) => f.toUpperCase()).join(', ')}
            </span>
            <span data-testid={`${baseTestId}-max-size`}>Maximum Size: {maxSizeMB} MB</span>
          </div>
        </>
      )}

      {!hideFileList && files.length > 0 && (
        <div
          className='file-upload__files'
          data-testid={`${baseTestId}-files`}>
          {files.map((uploadedFile) =>
            (() => {
              const scanStatusNotice = getScanStatusNotice(uploadedFile.scanStatus);
              const ScanStatusIcon =
                scanStatusNotice?.variant === 'error'
                  ? ErrorIcon
                  : scanStatusNotice?.variant === 'success'
                  ? SuccessIcon
                  : WarningIcon;

              return (
                <div
                  key={uploadedFile.id}
                  className={`file-upload__file ${uploadedFile.error ? 'file-upload__file--error' : ''} ${
                    uploadedFile.isUploading ? 'file-upload__file--uploading' : ''
                  }`}
                  data-testid={`${baseTestId}-file-${uploadedFile.id}`}>
                  <div
                    className='file-upload__file-icon'
                    data-testid={`${baseTestId}-file-icon-${uploadedFile.id}`}>
                    {uploadedFile.isUploading ? (
                      <Spinner
                        ariaLabel={`Uploading ${uploadedFile.name}`}
                        data-testid={`${baseTestId}-file-spinner-${uploadedFile.id}`}
                        size='medium'
                        variant='primary'
                      />
                    ) : (
                      getFileIconForExtension(uploadedFile.name, 40, `File: ${uploadedFile.name}`)
                    )}
                  </div>
                  <div
                    className='file-upload__file-details'
                    data-testid={`${baseTestId}-file-details-${uploadedFile.id}`}>
                    <div
                      className='file-upload__file-info'
                      data-testid={`${baseTestId}-file-info-${uploadedFile.id}`}>
                      <span
                        className='file-upload__file-name'
                        data-testid={`${baseTestId}-file-name-${uploadedFile.id}`}>
                        {onFileClick && isUploadedFilePreviewable(uploadedFile, { allowLocalFilePreview }) ? (
                          <TextLink
                            underline
                            ariaLabel={`Open file ${uploadedFile.name} in a new tab`}
                            className='file-upload__file-name-link'
                            data-testid={`${baseTestId}-file-name-link-${uploadedFile.id}`}
                            text={uploadedFile.name}
                            onClick={() => onFileClick(uploadedFile)}
                          />
                        ) : (
                          uploadedFile.name
                        )}
                        {uploadedFile.isUploading && (
                          <span
                            className='file-upload__uploading-text'
                            data-testid={`${baseTestId}-file-uploading-${uploadedFile.id}`}>
                            {' '}
                            — Uploading...
                          </span>
                        )}
                      </span>
                      {uploadedFile.error && !uploadedFile.isUploading && (
                        <Tooltip
                          isError
                          id={`error-tooltip-${uploadedFile.id}`}
                          placement='top'
                          title={uploadedFile.error}>
                          <ErrorIcon
                            ariaLabel={`Error: ${uploadedFile.error}`}
                            className='file-upload__file-error-icon'
                            size={16}
                          />
                        </Tooltip>
                      )}
                    </div>
                    {scanStatusNotice && (
                      <div
                        aria-live={scanStatusNotice.variant === 'error' ? 'assertive' : 'polite'}
                        className={`file-upload__scan-notice file-upload__scan-notice--${scanStatusNotice.variant}`}
                        data-testid={`${baseTestId}-file-scan-notice-${uploadedFile.id}`}
                        role='status'>
                        <ScanStatusIcon
                          ariaLabel=''
                          className='file-upload__scan-notice-icon'
                          size={18}
                        />
                        <span className='file-upload__scan-notice-text'>{scanStatusNotice.message}</span>
                      </div>
                    )}
                    {!uploadedFile.isUploading &&
                      !uploadedFile.error &&
                      !uploadedFile.isExisting &&
                      !scanStatusNotice &&
                      !uploadedFile.file?.size && (
                        <div
                          aria-live='polite'
                          className='file-upload__scan-notice file-upload__scan-notice--warning'
                          data-testid={`${baseTestId}-file-unreadable-notice-${uploadedFile.id}`}
                          role='status'>
                          <WarningIcon
                            ariaLabel=''
                            className='file-upload__scan-notice-icon'
                            size={18}
                          />
                          <span className='file-upload__scan-notice-text'>
                            The file could not be read. Remove and re-upload.
                          </span>
                        </div>
                      )}
                  </div>
                  {!uploadedFile.isUploading && (
                    <TextLink
                      underline
                      ariaLabel={`Remove file ${uploadedFile.name}`}
                      className='file-upload__file-remove-link'
                      data-testid={`${baseTestId}-file-remove-${uploadedFile.id}`}
                      text='Remove'
                      onClick={() => handleRemoveFile(uploadedFile.id)}
                    />
                  )}
                </div>
              );
            })(),
          )}
        </div>
      )}
    </div>
  );
}

export default FileUpload;
